"use client"

import { useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  CalculatorCard,
  CalculatorGrid,
  FormulaNote,
  NumberField,
  ResultStat,
  formatCurrency,
  parseNum,
} from "./calculator-ui"

/**
 * CPA = ad spend ÷ conversions, at the campaign level.
 *
 * Deliberately NOT the same tool as the CAC calculator. CAC answers "what does
 * a CUSTOMER cost", counting sales and marketing spend against new customers.
 * CPA answers "what does this ACTION cost in this ad account" — and the action
 * is whatever the campaign optimises for, which is often a lead or a signup
 * rather than a purchase.
 *
 * The second tab works the problem backwards: given a target CPA, how much can
 * you afford to spend, or how many conversions must a fixed budget return.
 */
export function CpaWidget() {
  // Actual CPA
  const [spend, setSpend] = useState("8000")
  const [conversions, setConversions] = useState("160")
  // Target planning
  const [targetCpa, setTargetCpa] = useState("40")
  const [plannedBudget, setPlannedBudget] = useState("8000")

  const cpa = (() => {
    const s = parseNum(spend)
    const c = parseNum(conversions)
    return s !== null && c !== null && c > 0 ? s / c : NaN
  })()

  const conversionsNeeded = (() => {
    const t = parseNum(targetCpa)
    const b = parseNum(plannedBudget)
    return t !== null && b !== null && t > 0 ? b / t : NaN
  })()

  return (
    <CalculatorCard title="CPA Calculator">
      <Tabs defaultValue="actual">
        <TabsList className="mb-6">
          <TabsTrigger value="actual">Actual CPA</TabsTrigger>
          <TabsTrigger value="target">Plan to a target CPA</TabsTrigger>
        </TabsList>

        <TabsContent value="actual">
          <CalculatorGrid>
            <NumberField
              id="cpa-spend"
              label="Ad spend"
              value={spend}
              onChange={setSpend}
              prefix="$"
            />
            <NumberField
              id="cpa-conversions"
              label="Conversions"
              value={conversions}
              onChange={setConversions}
              min={0}
              step={1}
            />
          </CalculatorGrid>
          <div className="mt-6">
            <ResultStat
              label="Cost per acquisition"
              value={formatCurrency(cpa)}
              caption={
                Number.isFinite(cpa)
                  ? `Each conversion costs ${formatCurrency(cpa)} in media. Compare it against what one conversion is worth, not against another account's CPA.`
                  : "Enter spend and a non-zero conversion count."
              }
            />
          </div>
          <FormulaNote>CPA = Ad spend ÷ Conversions, counted on the same conversion event the campaign optimises for.</FormulaNote>
        </TabsContent>

        <TabsContent value="target">
          <CalculatorGrid>
            <NumberField
              id="cpa-target"
              label="Target CPA"
              value={targetCpa}
              onChange={setTargetCpa}
              prefix="$"
            />
            <NumberField
              id="cpa-budget"
              label="Planned budget"
              value={plannedBudget}
              onChange={setPlannedBudget}
              prefix="$"
            />
          </CalculatorGrid>
          <div className="mt-6">
            <ResultStat
              label="Conversions needed to hit target"
              value={
                Number.isFinite(conversionsNeeded)
                  ? Math.round(conversionsNeeded).toLocaleString()
                  : "—"
              }
              caption={
                Number.isFinite(conversionsNeeded)
                  ? `This budget must return ${Math.round(conversionsNeeded).toLocaleString()} conversions to land on a ${formatCurrency(parseNum(targetCpa) ?? 0)} CPA. Fewer than that and your real CPA is above target.`
                  : "Enter a non-zero target CPA and a budget."
              }
            />
          </div>
          <FormulaNote>Conversions needed = Planned budget ÷ Target CPA.</FormulaNote>
        </TabsContent>
      </Tabs>
    </CalculatorCard>
  )
}
